import {
    Fragment,
    useEffect,
    useMemo,
    useState
} from "react";
import {
    Link,
    useNavigate,
    useParams
} from "react-router-dom";
import { useSelector } from "react-redux";
import { Tooltip } from "react-tooltip";

import {
    ArrowDownTrayIcon,
    ArrowPathIcon,
    PlusIcon,
    TrashIcon
} from "@heroicons/react/24/outline";
import { QuestionMarkCircleIcon } from "@heroicons/react/24/solid";

import {
    ConnectorEmailDigestAttachmentFileFormat,
    ConnectorType,
    ConnectorWebhookStorePayload,
    IConnectorDetails,
    IConnectorEmailDigestDetails,
    IConnectorMicrosoftGraphApiDetails,
    IConnectorWebhookDetails,
    IEmailDigestJobSlim,
    IMsGraphApiUserStats,
    IOrgUser,
    IWebhookCallLog
} from "../lib/backend/extractions.types.generated";
import {
    ORG_ROLES,
    ORG_TYPES,
    USER_ROLES
} from "../lib/consts";
import {
    Backend,
    BackendObj
} from "../lib/backend";
import {
    selectIsSidebarLarge,
    selectMemberships,
    selectUser
} from "../lib/scraper.slice";
import {
    classNames,
    getLocalHour,
    getUtcHour,
    prettyDateTime,
    validateEmail
} from "../lib/utils";

import { TwoRowHeader } from "../components/Header";
import { ConfirmModal } from "../components/ConfirmModal";
import { LoadingSpinner, LoadingSpinnerLimit } from "../components/LoadingSpinner";
import { Button, ButtonGroup, IconButton } from "../components/Button";
import { OrgPill } from "../components/OrgPill";
import { IntegerInput, Textbox } from "../components/Textbox";
import { Dropdown } from "../components/Dropdown";
import { EmailAddressListForm } from "../components/EmailAddressListForm";
import { ComboBox } from "../components/ComboBox";
import { Pagination } from "../components/Pagination";
import { TimeoutErrorMessageBar } from "../components/ErrorMessageBar";

export const getInitConnectorDetails = (type: ConnectorType): IConnectorDetails | undefined => {
    if (type === "email_digest") {
        return {
            utc_hour: getUtcHour(7),
            to_email_addresses: [],
            email_subject: "Daily Digest",
            file_format: "excel"
        } as IConnectorEmailDigestDetails
    } else if (type === "webhook") {
        return {
            webhook_url: "",
            version: "v2",
            retry_count: 0,
            store_payload: "none",
            notify_on_fail_email_address: ""
        } as IConnectorWebhookDetails
    } else if (type === "microsoft_graph_api") {
        return {
        } as IConnectorMicrosoftGraphApiDetails
    }
    return undefined;
}

type NewConnectorProps = {
    init_connector_type?: ConnectorType;
}

export function NewConnector({ init_connector_type }: NewConnectorProps) {
    const navigate = useNavigate();

    const { connector_uuid } = useParams<{ connector_uuid: string | undefined }>();
    const is_edit = connector_uuid !== undefined;

    const is_sidebar_large = useSelector(selectIsSidebarLarge);
    const memberships = useSelector(selectMemberships);

    // default is personal org, if not available, use first org
    const default_org_uuid =
        memberships.find((membership) => membership.org.type === ORG_TYPES.business && (membership.role === ORG_ROLES.editor || membership.role === ORG_ROLES.admin))?.org.uuid ||
        memberships.find((membership) => membership.org.type === ORG_TYPES.personal)?.org.uuid ||
        memberships[0].org.uuid ||
        "";
    const can_edit_orgs = memberships.filter((m) => (m.role === ORG_ROLES.admin || m.role === ORG_ROLES.editor)).map((m) => m.org);

    const [connector_name, setConnectorName] = useState<string>("");
    const [connector_type, setConnectorType] = useState<ConnectorType | undefined>(init_connector_type);
    const [connector_org_uuid, setConnectorOrgUuid] = useState<string>(default_org_uuid);
    const [connector_details, setConnectorDetails] = useState<IConnectorDetails | undefined>(undefined);
    const [connector_is_pinned, setConnectorIsPinned] = useState<boolean>(false);
    const [is_loading, setIsLoading] = useState<boolean>(true);
    const [is_init, setIsInit] = useState<boolean>(true);
    const [is_committing, setIsCommitting] = useState<boolean>(false);
    const [is_back_modal_open, setIsBackModalOpen] = useState<boolean>(false);
    const [is_remove_confirm_open, setIsRemoveConfirmOpen] = useState(false);
    const [remove_connector_name, setRemoveConnectorName] = useState("");

    useEffect(() => {
        if (is_edit) {
            setIsLoading(true);
            BackendObj.extractions.getConnector({ connector_uuid })
                .then(({ connector }) => {
                    if (connector !== undefined) {
                        const { name, type, org_uuid, details } = connector;
                        setConnectorName(name);
                        setConnectorType(type);
                        setConnectorOrgUuid(org_uuid);
                        setConnectorDetails(details);
                        setConnectorIsPinned(connector.is_pinned ?? false);
                    }
                    setIsLoading(false);
                });
        } else if (init_connector_type) {
            setConnectorDetails(getInitConnectorDetails(init_connector_type));
            setIsLoading(false);
        }
    }, [is_edit, connector_uuid, default_org_uuid, init_connector_type]);

    const refreshConnectorDetails = () => {
        if (connector_uuid) {
            BackendObj.extractions.getConnector({ connector_uuid })
                .then(({ connector }) => setConnectorDetails(connector?.details));
        }
    }

    const onCreateConnector = async () => {
        setIsCommitting(true);
        try {
            // make sure all required fields are set
            if (connector_name === "") { throw new Error("Connector name is required"); }
            if (connector_type === undefined) { throw new Error("Connector type is required"); }
            if (connector_details === undefined) { throw new Error("Connector details are required"); }
            // create connector
            const { connector_uuid } = await BackendObj.extractions.createConnector({
                name: connector_name,
                type: connector_type,
                org_uuid: connector_org_uuid,
                details: connector_details
            });
            // navigate to new connector
            navigate(`/connector/${connector_uuid}`);
        } catch (error) {
            console.error(error);
        } finally {
            setIsInit(true);
            setIsCommitting(false);
        }
    }

    const onUpdateConnector = async () => {
        setIsCommitting(true);
        try {
            // make sure all required fields are set
            if (connector_uuid === undefined) { throw new Error("Connector UUID is required"); }
            if (connector_name === "") { throw new Error("Connector name is required"); }
            if (connector_details === undefined) { throw new Error("Connector details are required"); }
            // update connector
            await BackendObj.extractions.updateConnector({
                connector_uuid,
                name: connector_name,
                details: connector_details
            });
            // navigate to updated connector
        } catch (error) {
            console.error(error);
        } finally {
            setIsInit(true);
            setIsCommitting(false);
        }
    }

    const toggleConnectorPin = async () => {
        if (connector_uuid === undefined) { return; }
        if (!connector_is_pinned) {
            await BackendObj.extractions.addElementToPinboard({
                pinned_type: "connector",
                pinned_uuid: connector_uuid
            });
            setConnectorIsPinned(true);
        } else {
            await BackendObj.extractions.removeElementFromPinboard({ pinned_uuid: connector_uuid });
            setConnectorIsPinned(false);
        }
    }

    const handleCheckBack = () => {
        if (is_init) {
            navigate("/endpoints");
        } else {
            setIsBackModalOpen(true);
        }
    }

    const handleBack = (result: boolean) => {
        if (result) {
            navigate("/endpoints");
        }
        setIsBackModalOpen(false);
    }

    const handleRemove = (result: boolean) => {
        if (result && connector_uuid !== undefined) {
            BackendObj.extractions.deleteConnector({ connector_uuid });
            navigate("/endpoints");
        }
        setIsRemoveConfirmOpen(false);
    }

    if (connector_type === undefined || connector_details === undefined) {
        if (is_loading) {
            return <div className={classNames("hidden lg:fixed lg:right-0 lg:inset-y-0 lg:flex lg:flex-row", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
                <LoadingSpinner />
            </div>;
        } else {
            return <div className="px-6 py-4">
                <div className="py-10">
                    <h2 className="text-base font-semibold leading-7 text-gray-900">Invalid Connector</h2>
                    <p className="mt-3 max-w-2xl text-sm leading-6 text-gray-600">
                        Connector does not exist.
                    </p>
                </div>
            </div>;
        }
    }

    const connector_display_type = (connector_type === "email_digest") ? "Email Digest" :
        (connector_type === "webhook") ? "Webhook" :
            (connector_type === "microsoft_graph_api") ? "Microsoft Graph API" : "Unknown";
    const connector_org = memberships.find(({ org }) => org.uuid === connector_org_uuid);

    // prepare Save template tooltip:
    let save_tooltip: string | undefined = undefined;
    if (connector_name === "") {
        save_tooltip = "Please provide a name for the connector";
    } else if (is_edit && !is_init) {
        save_tooltip = "Unsaved changes";
    }

    const is_valid = connector_name !== "" && (is_edit ? !is_init : true);

    return <div className={classNames("flex-row lg:fixed lg:right-0 lg:inset-y-0 overflow-y-auto", is_sidebar_large ? "lg:left-64" : "lg:left-20")}>
        <TwoRowHeader
            title={connector_name.length > 0 ? connector_name : "<no name>"}
            subtitle={`${is_edit ? "Edit" : "New"} ${connector_display_type} connector`}
            org_name={connector_org?.org.name}
            buttons={<Fragment>
                <ButtonGroup buttons={[
                    { icon: TrashIcon, text: "", skip: !is_edit, onClick: () => setIsRemoveConfirmOpen(true), tooltip: "Remove" }
                ]} />
                {!is_edit && <Button
                    text="Create"
                    highlight={true}
                    disabled={is_committing || !is_valid}
                    loading={is_committing}
                    onClick={onCreateConnector} />}
                {is_edit && <Button
                    text="Save Changes"
                    highlight={true}
                    disabled={is_committing || is_init || !is_valid}
                    loading={is_committing}
                    onClick={onUpdateConnector} />}
            </Fragment>}
            tooltip={save_tooltip}
            pinned={is_edit ? connector_is_pinned : undefined}
            onPin={is_edit ? toggleConnectorPin : undefined}
            onBack={handleCheckBack}
        />

        <ConfirmModal
            open={is_back_modal_open}
            title="Unsaved changes"
            message={["There are unsaved changes to the connector.", "Are you sure you want to go back?"]}
            cancel="No"
            confirm="Yes"
            onClose={handleBack} />
        <ConfirmModal open={is_remove_confirm_open}
            title="Are you sure you want to remove this connector?"
            message={["Are you sure you want to remove this connector?"]}
            confirm="Remove"
            disable_confirm={connector_name !== remove_connector_name}
            onClose={handleRemove}>
            <div className="mt-4">
                <label htmlFor="connector_name" className="block text-sm font-medium text-gray-700">
                    Please type the connector name to confirm deletion
                </label>
                <input
                    type="text"
                    name="connector_name"
                    id="connector_name"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-space_blue-500 focus:ring-space_blue-500 sm:text-sm"
                    value={remove_connector_name}
                    onChange={(e) => setRemoveConnectorName(e.target.value)}
                    placeholder="Enter connector name"
                />
            </div>
        </ConfirmModal>


        <div className="px-10 pt-10 max-w-5xl">
            {!is_edit && <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 pb-6">
                <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900 my-auto">
                    Organization
                </label>
                <div className="flex gap-2 mt-2 sm:col-span-2s sm:mt-0">
                    {can_edit_orgs.map((org, idx) => (<OrgPill
                        key={idx}
                        name={org.name}
                        type={org.type}
                        selected={org.uuid === connector_org_uuid}
                        onClick={() => { setIsInit(false); setConnectorOrgUuid(org.uuid); }}
                    />))}
                </div>
            </div>}

            <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 pb-8 mb-8 border-b border-gray-900/10">
                <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    Name
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <div className="flex flex-row items-center font-normal w-full pr-0.5">
                        <Textbox
                            placeholder="Please provide a name for the connector"
                            value={connector_name}
                            disabled={is_committing}
                            onChange={(value) => { setIsInit(false); setConnectorName(value); }} />
                    </div>
                </div>
            </div>
        </div>

        <div className="px-10 max-w-5xl">
            {connector_type === "email_digest" && <ConnectorEmailDigestDetails
                connector_uuid={connector_uuid}
                org_uuid={connector_org_uuid}
                details={connector_details as IConnectorEmailDigestDetails}
                updateDetails={(details) => { setIsInit(false); setConnectorDetails(details); }}
                refreshDetails={refreshConnectorDetails} />}
            {connector_type === "webhook" && <ConnectorWebhookDetails
                connector_uuid={connector_uuid}
                org_uuid={connector_org_uuid}
                details={connector_details as IConnectorWebhookDetails}
                updateDetails={(details) => { setIsInit(false); setConnectorDetails(details); }}
            />}
            {connector_type === "microsoft_graph_api" && <ConnectorMicrosoftGraphApiDetails
                connector_uuid={connector_uuid}
                org_uuid={connector_org_uuid}
                details={connector_details as IConnectorMicrosoftGraphApiDetails}
            />}
        </div>
    </div>;
}

type ConnectorEmailDigestDetailsProps = {
    connector_uuid?: string;
    org_uuid: string;
    details: IConnectorEmailDigestDetails;
    updateDetails(details: IConnectorEmailDigestDetails): void;
    refreshDetails(): void;
}

function ConnectorEmailDigestDetails({ connector_uuid, org_uuid, details, updateDetails, refreshDetails }: ConnectorEmailDigestDetailsProps) {
    const [org_users, setOrgUsers] = useState<IOrgUser[]>([]);
    const [email_digest_jobs, setEmailDigestJobs] = useState<IEmailDigestJobSlim[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [pagination, setPagination] = useState<{ offset: number, limit: number }>({ offset: 0, limit: 10 });
    const [is_sending_now, setIsSendingNow] = useState<boolean>(false);
    const [is_refreshing_jobs, setIsRefreshingJobs] = useState<boolean>(false);

    useEffect(() => {
        if (connector_uuid) {
            BackendObj.extractions.getEmailDigestJobs({ connector_uuid, offset: `${pagination.offset}`, limit: `${pagination.limit}` })
                .then(({ jobs, total }) => {
                    setEmailDigestJobs(jobs);
                    setTotal(total);
                });
        }
    }, [connector_uuid, pagination]);

    useEffect(() => {
        Backend.getOrgUsers({ org_uuid }).then(setOrgUsers);
    }, [org_uuid]);

    const updateToEmailAddress = (email_address_idx: number, email_address: string) => {
        if (email_address === "") {
            updateDetails({
                ...details,
                to_email_addresses: details.to_email_addresses.filter((_, idx) => idx !== email_address_idx)
            });
        } else {
            updateDetails({
                ...details,
                to_email_addresses: details.to_email_addresses.map((email, idx) => idx === email_address_idx ? email_address : email)
            });
        }
    }

    const addToEmailAddress = (email_address: string) => {
        if (email_address === "") { return; }
        updateDetails({
            ...details,
            to_email_addresses: [...details.to_email_addresses, email_address]
        });
    }

    const removeToEmailAddress = (email_address_idx: number) => {
        updateDetails({
            ...details,
            to_email_addresses: details.to_email_addresses.filter((_, idx) => idx !== email_address_idx)
        });
    }

    const sendNow = async () => {
        if (connector_uuid === undefined) { return; }
        setIsSendingNow(true);
        await BackendObj.extractions.sendEmailDigest({ connector_uuid });
        refreshDetails();
        const { jobs, total } = await BackendObj.extractions.getEmailDigestJobs({ connector_uuid, offset: `${pagination.offset}`, limit: `${pagination.limit}` });
        setEmailDigestJobs(jobs);
        setTotal(total);
        setIsSendingNow(false);
    }

    const refreshJobs = async () => {
        if (connector_uuid === undefined) { return; }
        setIsRefreshingJobs(true);
        const { jobs, total } = await BackendObj.extractions.getEmailDigestJobs({ connector_uuid, offset: `${pagination.offset}`, limit: `${pagination.limit}` });
        setEmailDigestJobs(jobs);
        setTotal(total);
        setIsRefreshingJobs(false);
    }

    return <div>
        <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 pb-6">
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                Hour when digest email is sent
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="flex flex-row items-center font-normal w-full pr-0.5">
                    <Dropdown
                        values={Array.from(Array(24).keys()).map((hour) => `${hour}:00`)}
                        ids={Array.from(Array(24).keys()).map((hour) => `${hour}`)}
                        selected={`${getLocalHour(details.utc_hour)}`}
                        onChange={(hour_utc) => updateDetails({ ...details, utc_hour: getUtcHour(parseInt(hour_utc, 10)) })} />
                </div>
            </div>
        </div>

        <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 pb-6">
            <label htmlFor="title" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Recipients</p>
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
                <EmailAddressListForm
                    selected={details.to_email_addresses}
                    available={org_users.map((user) => user.email)}
                    update={updateToEmailAddress}
                    remove={removeToEmailAddress}
                    add={addToEmailAddress}
                />
            </div>

            <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Subject</p>
            </label>
            <div className="sm:col-span-2 sm:mt-0">
                <div className="flex flex-col gap-y-4 w-full">
                    <Textbox
                        value={details.email_subject}
                        onChange={(email_subject) => updateDetails({ ...details, email_subject })}
                    />
                </div>
            </div>

            <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Email attachment format</p>
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
                <Dropdown
                    values={["Excel", "CSV (comma)", "CSV (semicolon)", "TSV"]}
                    ids={["excel", "csv", "csv-semicolon", "tsv"]}
                    selected={details.file_format}
                    onChange={(format) => updateDetails({ ...details, file_format: format as ConnectorEmailDigestAttachmentFileFormat })} />
            </div>
        </div>

        {connector_uuid !== undefined && <div className="max-w-5xl pb-6 border-t border-gray-900/10 pt-6">
            <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 pb-6">
                <label htmlFor="endpoint_forward_email_address" className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                    <p>Next digest</p>
                </label>
                <div className="mt-2 sm:col-span-2 sm:mt-0">
                    <div className="mt-2 sm:mt-0 sm:col-span-3 text-sm text-gray-500">
                        {details.next_ts ? prettyDateTime(details.next_ts) : "Not scheduled yet"}
                    </div>
                </div>
            </div>

            <div className="flex flex-row items-center gap-x-2 mt-6 justify-between pb-4">
                {total > 0 && <h4 className="text-sm font-medium text-gray-900">Digest Items</h4>}
                {total === 0 && <h4 className="text-sm font-medium text-gray-900">No digest items yet</h4>}
                <div className="flex flex-row items-center gap-x-2">
                    {total > 0 && <Button
                        text="Send now"
                        loading={is_sending_now}
                        onClick={sendNow} />}
                    <Button
                        text="Refresh"
                        loading={is_refreshing_jobs}
                        onClick={refreshJobs} />
                </div>
            </div>

            {total > 0 && <div className="mb-6 flex flex-col overflow-auto rounded-lg shadow ring-1 ring-black ring-opacity-5">
                <table className="min-w-full divide-y divide-gray-300 ">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-5"></th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">UUID</th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Time</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                        {email_digest_jobs.map((job, idx) => (
                            <tr key={idx}>
                                <td className="whitespace-nowrap py-3 pl-2 text-sm font-semibold text-gray-500 sm:pl-3">{idx + pagination.offset + 1}</td>
                                <td className="whitespace-nowrap px-3 py-3 text-xs font-mono text-gray-500">{job.uuid}</td>
                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">
                                    {job.status === "pending" ? <span className="text-gray-500">Pending</span> : <span className="text-green-500">Sent</span>}
                                </td>
                                <td className="whitespace-nowrap px-3 py-3 text-sm text-gray-500">{prettyDateTime(job.created_at)}</td>
                            </tr>))}
                    </tbody>
                </table>
                <Pagination
                    offset={pagination.offset}
                    setOffset={(offset) => setPagination({ ...pagination, offset })}
                    limit={pagination.limit}
                    setLimit={(offset, limit) => setPagination({ offset, limit })}
                    total={total}
                />
            </div>}
        </div>}
    </div >;
}


type ConnectorWebhookDetailsProps = {
    connector_uuid?: string;
    org_uuid: string;
    details: IConnectorWebhookDetails;
    updateDetails(details: IConnectorWebhookDetails): void;
}

function ConnectorWebhookDetails({ connector_uuid, org_uuid, details, updateDetails }: ConnectorWebhookDetailsProps) {
    const user = useSelector(selectUser);
    const is_admin = user.role === USER_ROLES.admin;
    const memberships = useSelector(selectMemberships);
    const is_org_admin = memberships.some((membership) => org_uuid === membership.org.uuid && membership.role === ORG_ROLES.admin);

    const [org_users, setOrgUsers] = useState<IOrgUser[]>([]);
    const [new_header_key, setNewHeaderKey] = useState<string>("");
    const [new_header_value, setNewHeaderValue] = useState<string>("");
    const [call_logs, setCallLogs] = useState<IWebhookCallLog[]>([]);
    const [total, setTotal] = useState<number>(0);
    const [pagination, setPagination] = useState<{ offset: number, limit: number }>({ offset: 0, limit: 10 });
    const [resend_webhook_info, setResendWebhookInfo] = useState<{ job_uuid: string, event_uuid: string } | undefined>(undefined);

    useEffect(() => {
        Backend.getOrgUsers({ org_uuid }).then(setOrgUsers);
    }, [org_uuid]);

    useEffect(() => {
        if (connector_uuid) {
            BackendObj.extractions.getWebhookLog({ webhook_connector_uuid: connector_uuid, offset: `${pagination.offset}`, limit: `${pagination.limit}` })
                .then(({ logs, total }) => {
                    console.log({ logs, total });
                    setCallLogs(logs);
                    setTotal(total);
                });
        }
    }, [connector_uuid, pagination]);

    const updateRetryCount = (value: number) => {
        updateDetails({ ...details, retry_count: value });
    };

    const updateStorePayload = (value: ConnectorWebhookStorePayload) => {
        updateDetails({ ...details, store_payload: value });
    };

    const updateVersion = (value: string) => {
        updateDetails({ ...details, version: value as "v1" | "v2" });
    };

    const updateNotifyOnFailEmailAddress = (value: string) => {
        updateDetails({ ...details, notify_on_fail_email_address: value });
    };

    const updateWebhookUrl = (value: string) => {
        updateDetails({ ...details, webhook_url: value });
    };

    const updateApiKey = (value: string) => {
        if (value === "") {
            updateDetails({ ...details, api_key: undefined });
        } else {
            updateDetails({ ...details, api_key: value });
        }
    }

    const isHeaderKeyValid = (key: string) => {
        return key.length > 0 && key.length < 100 && !key.includes(" ") && details.fixed_headers?.[key] === undefined;
    }

    const isHeaderValueValid = (value: string) => {
        return value.length < 1000;
    }

    const updateHeaderKey = (old_key: string, new_key: string) => {
        if (!isHeaderKeyValid(new_key)) { return; }
        const new_headers = { ...details.fixed_headers };
        delete new_headers[old_key];
        new_headers[new_key] = new_headers[old_key];
        updateDetails({ ...details, fixed_headers: new_headers });
    }

    const updateHeaderValue = (key: string, value: string) => {
        if (!isHeaderValueValid(value)) { return; }
        const new_headers = { ...details.fixed_headers };
        new_headers[key] = value;
        updateDetails({ ...details, fixed_headers: new_headers });
    }

    const removeHeader = (key: string) => {
        const new_headers = { ...details.fixed_headers };
        delete new_headers[key];
        updateDetails({ ...details, fixed_headers: new_headers });
    }

    const addHeader = () => {
        if (new_header_key.length === 0) { return; }
        updateDetails({ ...details, fixed_headers: { ...details.fixed_headers, [new_header_key]: new_header_value } });
        setNewHeaderKey("");
        setNewHeaderValue("");
    }

    const refreshCallLogs = () => {
        if (!connector_uuid) { return; }
        BackendObj.extractions.getWebhookLog({ webhook_connector_uuid: connector_uuid, offset: `${pagination.offset}`, limit: `${pagination.limit}` })
            .then(({ logs, total }) => {
                setCallLogs(logs);
                setTotal(total);
            });
    }

    const resendWebhook = (job_uuid: string, event_uuid: string) => {
        if (!connector_uuid) { return; }
        BackendObj.extractions.resendWebhookData({ job_uuid, event_uuid })
            .then(() => {
                refreshCallLogs();
            });
    }

    return <div>
        <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-4 pb-6">
            <label htmlFor="endpoint_webhook_url" className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Webhook URL</p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-2 text-gray-400"
                    data-tooltip-id="webhook-connector-details-tooltip-id"
                    data-tooltip-html={`
                        <p class="pb-4 max-w-sm">
                        This is the URL where we will send the data after processing.
                        If confirmation is required, we will send the data only after confirmation.
                        </p>`}
                />
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
                <Textbox
                    id="endpoint_webhook_url"
                    value={details.webhook_url}
                    onChange={(value) => updateWebhookUrl(value)}
                />
            </div>
        </div>

        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 pb-6">
            <label htmlFor="endpoint_webhook_url" className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Webhook API key</p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-2 text-gray-400"
                    data-tooltip-id="webhook-connector-details-tooltip-id"
                    data-tooltip-html={`
                        <p class="pb-4 max-w-sm">
                        Optional. This key is inserted into HTTP header ("X-API-KEY") of webhook calls.
                        </p>`}
                />
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
                <Textbox
                    id="endpoint_webhook_api_key"
                    value={details.api_key ?? ""}
                    onChange={(value) => updateApiKey(value)}
                />
            </div>
        </div>

        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 pb-6">
            <label htmlFor="endpoint_webhook_notify_on_fail_email_address" className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Fail notify email</p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-2 text-gray-400"
                    data-tooltip-id="webhook-connector-details-tooltip-id"
                    data-tooltip-html={`
                        <p class="pb-4 max-w-sm">
                        Email address that will receive notifications when webhook call fails.
                        </p>`}
                />
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0">
                <div className="flex flex-col gap-y-2 w-full max-w-2xl">
                    <ComboBox
                        values={["", ...org_users.map(user => user.email)]}
                        selected={details.notify_on_fail_email_address}
                        validate={validateEmail}
                        onChange={(user_email) => updateNotifyOnFailEmailAddress(user_email)} />
                </div>
            </div>
        </div>

        <div className="sm:grid sm:grid-cols-6 sm:items-start sm:gap-4 pb-6">
            <label className="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Format version</p>
            </label>
            <div className="mt-2 sm:mt-0">
                <div className="w-20">
                    <Dropdown
                        values={["v1", "v2"]}
                        selected={details.version}
                        onChange={(version: string) => updateVersion(version === "v1" ? "v1" : "v2")} />
                </div>
            </div>
            <label className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Retry count</p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-2 text-gray-400"
                    data-tooltip-id="webhook-connector-details-tooltip-id"
                    data-tooltip-html={`
                        <p class="pb-4 max-w-sm">
                        Number of times we will retry webhook call in case of failure.
                        Default is 0 (no retries). Maximum is 10.
                        </p>`}
                />
            </label>
            <div className="mt-2 sm:mt-0">
                <div className="w-20">
                    <IntegerInput
                        value={details.retry_count}
                        onChange={(value) => updateRetryCount(value)}
                    />
                </div>
            </div>

            <label htmlFor="endpoint_webhook_store_payload" className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Debug logging</p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-2 text-gray-400"
                    data-tooltip-id="webhook-connector-details-tooltip-id"
                    data-tooltip-html={`
                        <p class="pb-4 max-w-sm">
                        Log all or only failed webhook calls and responses for 7 days. Logs are available under endpoint job details.
                        </p>`}
                />
            </label>
            <div className="mt-2 sm:mt-0">
                <Dropdown
                    values={["None", "All", "Only Failed"]}
                    ids={["none", "all", "only_failed"]}
                    selected={details.store_payload}
                    onChange={(type) => updateStorePayload(type as ConnectorWebhookStorePayload)} />
            </div>
        </div>

        <div className="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 pb-10">
            <label htmlFor="endpoint_webhook_fixed_headers" className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Fixed headers</p>
                <QuestionMarkCircleIcon
                    className="w-5 h-5 ml-2 text-gray-400"
                    data-tooltip-id="webhook-connector-details-tooltip-id"
                    data-tooltip-html={`
                        <p class="pb-4 max-w-sm">
                        Add fixed HTTP headers to the webhook call. These headers cannot be overwritten from outside, e.g. from incoming REST API request.
                        </p>`}
                />
            </label>
            <div className="sm:col-span-2 sm:pt-1.5">
                <table className="min-w-full">
                    <thead>
                        <tr>
                            <th scope="col" className="py-1 pb-3 text-left text-sm font-medium text-gray-900">Key</th>
                            <th scope="col" className="py-1 pb-3 text-left text-sm font-medium text-gray-900">Value</th>
                        </tr>
                    </thead>
                    <tbody className="">
                        {Object.entries(details.fixed_headers ?? {}).map(([key, value], index) => (
                            <tr key={index}>
                                <td className="py-1 pr-2 whitespace-normal overflow-wrap hover:bg-sea_blue-100 text-left text-sm align-top cursor-text">
                                    <Textbox value={key} onChange={(new_key) => updateHeaderKey(key, new_key)} />
                                </td>
                                <td className="py-1 pr-1 whitespace-normal overflow-wrap hover:bg-sea_blue-100 text-left text-sm align-top cursor-text">
                                    <Textbox value={value} onChange={(value) => updateHeaderValue(key, value)} />
                                </td>
                                <td className="whitespace-nowrap px-3 w-10">
                                    <IconButton icon={TrashIcon} onClick={() => removeHeader(key)} />
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td className="py-1 pr-2 whitespace-normal overflow-wrap hover:bg-sea_blue-100 text-left text-sm align-top cursor-text">
                                <Textbox value={new_header_key} onChange={(value) => setNewHeaderKey(value)} placeholder="HTTP header name" />
                            </td>
                            <td className="py-1 pr-1 whitespace-normal overflow-wrap hover:bg-sea_blue-100 text-left text-sm align-top cursor-text">
                                <Textbox value={new_header_value} onChange={(value) => setNewHeaderValue(value)} placeholder="HTTP header fixed value" />
                            </td>
                            <td className="whitespace-nowrap px-3 w-10">
                                <IconButton icon={PlusIcon} onClick={addHeader} disabled={!isHeaderKeyValid(new_header_key) || !isHeaderValueValid(new_header_value)} />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            {total === 0 && <div className="flex flex-row items-center gap-x-2 mt-6 justify-between pb-4 col-span-3">
                <h4 className="text-sm font-medium text-gray-900">No webhook calls yet</h4>
                <div className="flex flex-row items-center gap-x-2">
                    <Button
                        text="Refresh"
                        onClick={refreshCallLogs} />
                </div>
            </div>}
        </div>

        {total > 0 && <div className="mb-6 flex flex-col overflow-auto rounded-lg shadow ring-1 ring-black ring-opacity-5">
            <table className="min-w-full divide-y divide-gray-300 ">
                <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-5"></th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Time</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Workflow</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">User</th>
                        <th scope="col" className="px-3 py-3.5 text-sm font-semibold text-gray-900 w-32 text-right">
                            <Button text="Refresh" onClick={refreshCallLogs} />
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {call_logs.map((log, idx) => (
                        <tr key={idx}>
                            <td className="whitespace-nowrap py-2 pl-2 text-sm font-semibold text-gray-500 sm:pl-3">{idx + pagination.offset + 1}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-sm text-gray-500">{prettyDateTime(log.call_ts)}</td>
                            <td className="px-3 py-2 text-sm text-gray-500">
                                <Link to={`/endpoint/${log.endpoint_uuid}`} className="hover:underline">{log.endpoint_name}</Link> | <Link to={`/template/${log.template_uuid}`} className="hover:underline">{log.template_name}</Link>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-500">
                                {is_admin && <Link to={`/admin/user/${log.user_uuid}`} className="hover:underline">{log.user_email}</Link>}
                                {!is_admin && <span>{log.user_email}</span>}
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-500 w-32 text-right">
                                {log.job_event_details.webhook_log_blob_id && <Button
                                    icon={ArrowDownTrayIcon}
                                    tooltip="Download webhook log"
                                    href={`/api/download/webhook_log?webhook_log_blob_id=${log.job_event_details.webhook_log_blob_id}`}
                                    open_in_new_tab={true}
                                />}
                                {(is_admin || is_org_admin) && <Button
                                    icon={ArrowPathIcon}
                                    tooltip="Resend webhook"
                                    onClick={() => setResendWebhookInfo({ job_uuid: log.job_uuid, event_uuid: log.job_event_uuid })}
                                />}
                            </td>
                        </tr>))}
                </tbody>
            </table>
            <Pagination
                offset={pagination.offset}
                setOffset={(offset) => setPagination({ ...pagination, offset })}
                limit={pagination.limit}
                setLimit={(offset, limit) => setPagination({ offset, limit })}
                total={total} />
        </div>}

        <ConfirmModal
            open={resend_webhook_info !== undefined}
            title="Resend webhook"
            message={[
                "Are you sure you want to resend the webhook?",
                "Resending can have unintended consequences, e.g. duplicating records in ERP or CRM.",
            ]}
            confirm="Resend"
            cancel="Cancel"
            onClose={(result) => {
                if (result && resend_webhook_info) {
                    resendWebhook(resend_webhook_info.job_uuid, resend_webhook_info.event_uuid);
                }
                setResendWebhookInfo(undefined);
            }}
        />

        <Tooltip id="webhook-connector-details-tooltip-id" />
    </div>;
}


type ConnectorMicrosoftGraphApiDetailsProps = {
    connector_uuid?: string;
    org_uuid: string;
    details: IConnectorMicrosoftGraphApiDetails;
}

function ConnectorMicrosoftGraphApiDetails({ connector_uuid, org_uuid, details: _details }: ConnectorMicrosoftGraphApiDetailsProps) {
    const [org_users, setOrgUsers] = useState<IOrgUser[]>([]);
    const [user_stats, setUserStats] = useState<IMsGraphApiUserStats[] | undefined>(undefined);
    const [is_refreshing_user_stats, setIsRefreshingUserStats] = useState<boolean>(false);
    const [is_testing, setIsTesting] = useState<boolean>(false);
    const [test_result, setTestResult] = useState<{ success: boolean, message: string } | undefined>(undefined);
    const [token_user_uuid, setTokenUserUuid] = useState<string>("");
    const [simple_test_token_user_uuid, setSimpleTestTokenUserUuid] = useState<string>("");
    const [to_email_address, setToEmailAddress] = useState<string | undefined>(undefined);
    const [send_as_user_uuid, setSendAsUserUuid] = useState<string>("");
    const is_valid_to_email_address = useMemo(() => { return validateEmail(to_email_address ?? "") }, [to_email_address]);
    const [is_sending_test_email, setIsSendingTestEmail] = useState<boolean>(false);
    const [test_email_result, setTestEmailResult] = useState<{ success: boolean, message: string } | undefined>(undefined);

    const [error_message, setErrorMessage] = useState<string | undefined>(undefined);

    useEffect(() => {
        Backend.getOrgUsers({ org_uuid }).then(setOrgUsers);
    }, [org_uuid]);

    useEffect(() => {
        if (!connector_uuid) { return; }
        BackendObj.extractions.getMicrosoftGraphApiStats({ connector_uuid }).then(({ stats }) => { setUserStats(stats.users); });
    }, [connector_uuid]);

    const execTest = () => {
        if (!connector_uuid || !simple_test_token_user_uuid) { return; }
        setIsTesting(true);
        setErrorMessage(undefined);
        setTestResult(undefined);
        BackendObj.extractions.testMicrosoftGraphApiConnector({
            connector_uuid,
            token_user_uuid: simple_test_token_user_uuid
        })
            .then(({ success, message }) => {
                setIsTesting(false);
                setTestResult({ success, message: message ?? "Test successful" });
            })
            .catch((error) => {
                setIsTesting(false);
                setErrorMessage(error.message);
            });
    }

    const execSendTestEmail = () => {
        if (!connector_uuid || !token_user_uuid || !to_email_address || !send_as_user_uuid) { return; }
        setIsSendingTestEmail(true);
        setErrorMessage(undefined);
        setTestEmailResult(undefined);
        BackendObj.extractions.sendTestEmailForMicrosoftGraphApiConnector({
            connector_uuid,
            token_user_uuid,
            to_email_address,
            send_as_user_uuid
        })
            .then(({ success, message }) => {
                setIsSendingTestEmail(false);
                setTestEmailResult({ success, message: message ?? "Test email sent successfully" });
            })
            .catch((error) => {
                setIsSendingTestEmail(false);
                setErrorMessage(error.message);
            });
    }

    const refreshUserStats = async () => {
        if (!connector_uuid) { return; }
        setIsRefreshingUserStats(true);
        setUserStats(undefined);
        const { stats } = await BackendObj.extractions.getMicrosoftGraphApiStats({ connector_uuid });
        setUserStats(stats.users);
        setIsRefreshingUserStats(false);
    }

    if (connector_uuid === undefined) { return null; }

    return <div>
        <div className="sm:grid sm:grid-cols-3 max-w-5xl sm:items-start sm:gap-x-4 sm:gap-y-8 pb-6">
            <label htmlFor="graph_api_user_test_email" className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Test connector</p>
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 flex flex-col gap-y-4">
                <div className="flex flex-row items-center justify-end gap-x-4">
                    <Dropdown
                        id="graph_api_simple_test_token_user"
                        values={org_users.map(user => user.email)}
                        ids={org_users.map(user => user.uuid)}
                        selected={simple_test_token_user_uuid ?? ""}
                        placeholder="Select the token to test"
                        onChange={(value) => setSimpleTestTokenUserUuid(value)}
                    />
                    <Button
                        text="Test"
                        onClick={execTest}
                        loading={is_testing}
                        disabled={!simple_test_token_user_uuid || is_testing}
                    />
                </div>
                {(test_result?.success === true || test_result?.success === false || test_result?.message) && (
                    <div className="flex flex-row items-center gap-x-4">
                        <div className="flex-grow">
                            {test_result?.success === true && <p className="text-sm text-green-500">Test successful</p>}
                            {test_result?.success === false && <p className="text-sm text-red-500">Test failed</p>}
                            {test_result?.message && <p className="text-sm text-gray-500">{test_result.message}</p>}
                        </div>
                    </div>
                )}
            </div>

            <label htmlFor="graph_api_user_test_email" className="flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5">
                <p>Send test email</p>
            </label>
            <div className="mt-2 sm:col-span-2 sm:mt-0 flex flex-col gap-y-4">
                <div className="flex flex-row items-center gap-x-2">
                    <p className="w-32 text-sm text-gray-900">Token:</p>
                    <Dropdown
                        id="graph_api_token_user"
                        values={org_users.map(user => user.email)}
                        ids={org_users.map(user => user.uuid)}
                        selected={token_user_uuid ?? ""}
                        placeholder="Token that will be used to call the Graph API"
                        onChange={(value) => setTokenUserUuid(value)}
                    />
                </div>
                <div className="flex flex-row items-center gap-x-2">
                    <p className="w-32 text-sm text-gray-900">To:</p>
                    <Textbox
                        id="graph_api_user_test_email"
                        value={to_email_address ?? ""}
                        onChange={(value) => setToEmailAddress(value)}
                        placeholder="Email address to send the test email to"
                    />
                </div>
                <div className="flex flex-row items-center gap-x-2">
                    <p className="w-32 text-sm text-gray-900">Send as:</p>
                    <Dropdown
                        id="graph_api_send_as_user"
                        values={org_users.map(user => user.email)}
                        ids={org_users.map(user => user.uuid)}
                        selected={send_as_user_uuid ?? ""}
                        placeholder="Select user to send the test email as"
                        onChange={(value) => setSendAsUserUuid(value)}
                    />
                </div>
                <div className="mt-2 sm:col-span-2 sm:mt-0 flex flex-row items-center gap-x-4">
                    {test_email_result?.success === true && <p className="text-sm text-green-500">Test email sent successfully</p>}
                    {test_email_result?.success === false && <p className="text-sm text-red-500">Test email failed</p>}
                    {test_email_result?.message && <p className="text-sm text-gray-500">{test_email_result.message}</p>}
                    <div className="flex-grow" />
                    <Button
                        text="Send"
                        onClick={execSendTestEmail}
                        loading={is_sending_test_email}
                        disabled={!token_user_uuid || !to_email_address || !is_valid_to_email_address || !send_as_user_uuid || is_sending_test_email}
                    />
                </div>
            </div>


            <div className="pt-6 flex flex-row items-center text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:col-span-3">
                <p>Token details for connected users</p>
                <div className="flex-grow" />
                <Button
                    text="Refresh"
                    onClick={refreshUserStats}
                    disabled={is_refreshing_user_stats}
                />
            </div>

            {user_stats === undefined && <div className="flex flex-row items-center gap-x-2 mt-6 justify-between pb-4 col-span-3">
                <LoadingSpinnerLimit />
            </div>}

            {user_stats?.length === 0 && <div className="flex flex-row items-center gap-x-2 mt-6 justify-between pb-4 col-span-3">
                <h4 className="text-sm font-medium text-gray-900">No tokens found</h4>
            </div>}
        </div>

        {user_stats !== undefined && user_stats.length > 0 && <div className="mb-6 flex flex-col overflow-auto rounded-lg shadow ring-1 ring-black ring-opacity-5">
            <table className="min-w-full divide-y divide-gray-300 ">
                <thead className="bg-gray-50">
                    <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 w-5"></th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">User</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Created</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Expires</th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">In cache</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                    {user_stats.map((user, idx) => (
                        <tr key={idx}>
                            <td className="whitespace-nowrap py-3 pl-2 text-sm font-semibold text-gray-500 sm:pl-3">{idx + 1}</td>
                            <td className="whitespace-nowrap p-3 text-sm text-gray-500">{user.email}</td>
                            <td className="whitespace-nowrap p-3 text-sm text-gray-500">{prettyDateTime(user.refresh_token_created_at)}</td>
                            <td className="whitespace-nowrap p-3 text-sm text-gray-500">{prettyDateTime(user.refresh_token_expires_at)}</td>
                            <td className="whitespace-nowrap p-3 text-sm text-gray-500">{user.access_token_in_cache ? "Yes" : "No"}</td>
                        </tr>))}
                </tbody>
            </table>
        </div>}

        <TimeoutErrorMessageBar message={error_message} clearMessage={() => setErrorMessage(undefined)} />
    </div>;
}